import React, { useCallback, useMemo } from 'react';

// Styles & Assets
import styles from './UserAdministrationPage.module.css';

// Services
import { getEnvironmentApiUrl } from '../../services/General';
import { isResidenceFeatureEnabled } from '../../services/residences';

// Hooks
import { useState, useEffect, useReducer } from 'react';
import useHttp from '../../hooks/useHttp';
import useResidences from '../../hooks/useResidences';

// Components
import MultiSelector from '../../components/UI/MultiSelector/MultiSelector';
import ErrorSpinnerWrapper from '../../components/UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import ToolbarUserAdministration from '../../components/UserAdministrationPage/ToolbarUserAdministration/ToolbarUserAdministration';
import Table from '../../components/UI/Table/Table';
import UserActions from '../../components/UserAdministrationPage/UserActions/UserActions';

// Interfaces
import { UserData, UsersFiltersDispatch } from '../../interfaces/UserInfo';
import { Column } from '../../interfaces/Columns';
import { FiltersUsers } from '../../interfaces/Filters';

// Language
import { useTranslation } from 'react-i18next';
import { getFullName } from '../../services/getPatientAtribute';

const initUsersFilters = {
    filterSearchbar: () => () => true,
    currentFiltersActive: [],
    currentFiltersRole: [],
};

const usersFiltersReducer = (state: FiltersUsers, action: UsersFiltersDispatch): FiltersUsers => {
    const currentFilters = action.currentFilters || [];
    if (action.type === 'ACTIVE') return { ...state, currentFiltersActive: currentFilters };
    else if (action.type === 'ROLE') return { ...state, currentFiltersRole: currentFilters };
    else if (action.type === 'SEARCHBAR') return { ...state, filterSearchbar: action.filter };
    else if (action.type === 'DELETE_FILTERS') return { ...initUsersFilters, filterSearchbar: state.filterSearchbar };
    else if (action.type === 'DELETE') return initUsersFilters;
    else return state;
};


// UserAdministrationPage: renders the user Toolbar and the UserAdministrationTable
// component. It requests the users information and creates the columns of the table.
// It also handles the searchbar filters.
const UserAdministrationPage: React.FC = () => {

    const { t } = useTranslation();

    const { isLoading, error, sendRequest: getUsers } = useHttp<UserData[]>();

    const [users, setUsers] = useState<UserData[]>([]);

    const updateUser = useCallback((newUserData: UserData) => {
        setUsers(oldUsers => {
            const newUsers = [...oldUsers];
            return newUsers.map(newUser =>
                newUser.id === newUserData.id ?
                    { ...newUser, ...newUserData }
                    : newUser);
        });
    }, []);

    const getReferenceDoctorName = useCallback((referenceDoctorID: UserData['reference_doctor_id']) => {
        const referenceDoctor = users.find(u => u.id === referenceDoctorID);
        if (!referenceDoctor) return '-';
        return `${referenceDoctor?.first_name || ''} ${referenceDoctor?.last_name_1 || ''}`
    }, [users]);

    const { residences, linkUser } = useResidences();

    const residenceEnabled = isResidenceFeatureEnabled();

    const DEFAULT_COLUMNS_USER: Column<UserData>[] = useMemo(() => [
        {
            id: 0,
            name: () => t('active'),
            visible: true,
            editVisiblilityDisabled: true,
            sortable: false,
            classHeader: styles.centerActiveHeader,
            classBody: styles.centerActiveCell,
            element: (user) => (
                <div className='center'>
                    <div className={user.active ? 'smallCircle activeCircle' : 'smallCircle inactiveCircle'}>
                        {user.active}
                    </div>
                </div>),
        },
        {
            id: 1,
            name: () => t('actions'),
            visible: true,
            sortable: false,
            classHeader: styles.centerActiveHeader,
            editVisiblilityDisabled: true,
            element: (user) => <UserActions
                user={user}
                updateUser={updateUser}
            />
        },
        {
            id: 2,
            name: () => t('ID'),
            visible: false,
            sortable: true,
            cell: (user) => `${user.id}`,
        },
        {
            id: 3,
            name: () => t('name'),
            visible: true,
            sortable: true,
            cell: (user) => getFullName(user),
        },
        {
            id: 4,
            name: () => t('service'),
            visible: true,
            sortable: true,
            cell: () => '',
        },
        {
            id: 5,
            name: () => t('phone'),
            visible: false,
            sortable: true,
            cell: (user) => user.phone || '',
        },
        {
            id: 6,
            name: () => t('email'),
            visible: false,
            sortable: true,
            cell: (user) => (user).email || '',
        },
        {
            id: 7,
            name: () => t('role'),
            visible: true,
            sortable: true,
            cell: (user) => `${(user.user_roles?.map(r => r.role?.name ?? '').join(', ') || '')}`,
        },
        {
            id: 8,
            name: () => t('residences'),
            sortable: false,
            classBody: styles.multiselectorWrapper,
            visible: residenceEnabled,
            editVisiblilityDisabled: !residenceEnabled,
            element: (user) => {
                const optionsWithChecked = residences.map(r => ({ id: r.id, name: r.name, checked: r.users?.some(u => u.id === user.id) ?? false }))
                return <MultiSelector
                    options={optionsWithChecked}
                    handleChange={(id) => linkUser(user, id)}
                    disabledAllOption
                />
            }
        },
        {
            id: 9,
            name: () => t('reference'),
            visible: false,
            sortable: true,
            cell: (user) => (user).is_reference_doctor ? t('yes') : t('no'),
        },
        {
            id: 10,
            name: () => t('reference_doctor'),
            visible: true,
            sortable: true,
            cell: (user) => getReferenceDoctorName(user.reference_doctor_id),
        }
    ], [getReferenceDoctorName, linkUser, residenceEnabled, residences, t, updateUser]);

    const [columnsUser, setColumnsUser] = useState<Column<UserData>[]>(DEFAULT_COLUMNS_USER);

    useEffect(() => {
        setColumnsUser(DEFAULT_COLUMNS_USER)
    }, [DEFAULT_COLUMNS_USER]);

    // filters users
    const [filtersUsers, dispatchUsersFilters] =
        useReducer(usersFiltersReducer, initUsersFilters);

    const toggleUsersVisibleColumn = (id: number) => {
        setColumnsUser(oldColumns => {
            const newColumns = [...oldColumns];
            return newColumns.map(column => {
                if (column.id !== id) return column;
                const newColumn = { ...column };
                return {
                    ...newColumn,
                    visible: !newColumn.visible
                };
            });
        });
    };

    useEffect(() => {
        getUsers({
            url: getEnvironmentApiUrl() + 'user/all',
            method: 'POST',
            body: { params: {}, filter: { enabled_bchome: true } },
        }, (data) => setUsers(data));
    }, [getUsers]);

    const getFilterRole = () => {
        const currentFilters = filtersUsers.currentFiltersRole;
        return (currentFilters.length !== 1) ? () => true :
            (user: UserData) => user.user_roles.some(r => r.role_id === currentFilters[0]);
    };

    const getFilterActive = () => {
        const currentFilters = filtersUsers.currentFiltersActive;
        return (currentFilters.length !== 1) ? () => true :
            (user: UserData) => user.active === (currentFilters[0] === 0);
    };

    const filteredUsers = users.filter(user => {
        return getFilterActive()(user) && getFilterRole()(user) && filtersUsers.filterSearchbar?.()(user);
    });


    return (
        <div className='pageContainer'>
            <ToolbarUserAdministration
                filtersUsers={filtersUsers}
                dispatchUsersFilters={dispatchUsersFilters}
                columnsUser={columnsUser}
                toggleUsersVisibleColumn={toggleUsersVisibleColumn}
            />
            <ErrorSpinnerWrapper
                error={error}
                loading={isLoading}
            >
                <Table
                    columns={columnsUser}
                    rows={filteredUsers}
                    nohoverRow
                />
            </ErrorSpinnerWrapper>
        </div>
    );
};

export default UserAdministrationPage;

